import { inject } from 'vue'
import { useToast } from 'vue-toastification-original'

let activeModalToast = null
let pendingToasts = []
let _globalToast = null

const TOAST_METHODS = ['success', 'error', 'warning', 'info']
const TOAST_TIMEOUT = 3000

export function setActiveModalToast(toastInterface) {
  activeModalToast = toastInterface
  pendingToasts = []
}

export function clearActiveModalToast() {
  activeModalToast = null
}

/**
 * Re-fires any recent modal toasts on the global container so they
 * persist after the modal closes. Should be called before clear().
 */
export function flushPendingToasts() {
  if (!_globalToast || !pendingToasts.length) {
    return
  }

  const now = Date.now()
  pendingToasts
    .filter((t) => now - t.time < TOAST_TIMEOUT)
    .forEach(({ method, args }) => _globalToast[method](...args))
  pendingToasts = []
}

/**
 * Modal-aware toast composable. Resolves to the correct toast container at
 * call time: the modal's own container (via inject) if inside a modal, the
 * globally active modal toast (for non-component contexts like axios), or
 * the default global toast instance.
 *
 * Toasts fired within a modal are tracked so they can be re-fired on the
 * global container when the modal closes (see flushPendingToasts).
 */
export default function useGlobalToast() {
  const injectedModalToastRef = inject('modalToast', null)
  const globalToast = useToast()
  _globalToast = globalToast

  return new Proxy(
    {},
    {
      get(target, prop) {
        let toastInterface
        let isModalToast = false

        if (injectedModalToastRef?.value) {
          toastInterface = injectedModalToastRef.value
          isModalToast = true
        } else if (activeModalToast) {
          toastInterface = activeModalToast
          isModalToast = true
        } else {
          toastInterface = globalToast
        }

        const value = toastInterface[prop]
        if (typeof value !== 'function') {
          return value
        }

        if (isModalToast && TOAST_METHODS.includes(prop)) {
          return (...args) => {
            pendingToasts.push({ method: prop, args, time: Date.now() })
            return value.call(toastInterface, ...args)
          }
        }

        return value.bind(toastInterface)
      },
    }
  )
}
